import React, { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";

/**
 * SpmProfileTable
 * ───────────────
 * Renders the SPM (Shortest Path Mapping) results as a summary strip plus a
 * sortable/paginated DataTable. Each row has a "View ego network" action so
 * the parent can drill into a single residue's neighborhood.
 *
 * Props
 *   tableData       Array returned by the `spm` analysis mode. The first
 *                   element (if present) is the summary row — identified
 *                   either by col1 === "__summary__" or by the caller
 *                   pre-splitting it out via `summary` prop.
 *   summary         (optional) object with { totalResidues, hubCount,
 *                   meanBtw, spmEdges, percentile, scope }. If omitted,
 *                   derived from the summary row.
 *   percentile      Default percentile (10) — used only for the column label.
 *   onSelectResidue (position) => void — called when the user clicks "View".
 *   loading         Boolean — forwards to the DataTable.
 */
const SpmProfileTablePath = ({
    tableData = [],
    summary: summaryProp,
    percentile = 10,
    onSelectResidue,
    loading = false,
}) => {
    // 1. Definition of extra integrative columns
    const integrativeColumns = [
        { field: "col8", header: "PFam HMM Pos" },
      //  { field: "mochi_folding", header: "MoCHI Folding" },
      //  { field: "mochi_binding", header: "MoCHI Binding" },
        { field: "col9", header: "AlphaMissense Class" },
        { field: "col10", header: "AM Pathogenic Frac" }
    ];

    // State to control which extra columns are visible
    const [selectedColumns, setSelectedColumns] = useState([]);

    // 2. Original summary processing logic
    const { rows, summary } = useMemo(() => {
        if (!Array.isArray(tableData) || tableData.length === 0) {
            return { rows: [], summary: summaryProp || null };
        }

        const first = tableData[0];
        if (first && first.col1 === "__summary__") {
            const asObj = {};
            ["col2", "col3", "col4", "col5", "col6", "col7"].forEach((k) => {
                const raw = first[k];
                if (typeof raw === "string") {
                    raw.split(/\s+/).forEach((tok) => {
                        const [key, val] = tok.split("=");
                        if (key && val !== undefined) asObj[key] = val;
                    });
                }
            });
            return {
                rows: tableData.slice(1),
                summary: summaryProp || {
                    totalResidues: parseInt(asObj.nodes, 10) || 0,
                    totalEdges: parseInt(asObj.edges, 10) || 0,
                    hubCount: parseInt(asObj.hubs, 10) || 0,
                    spmEdges: parseInt(asObj.spm_edges, 10) || 0,
                    meanBtw: parseFloat(asObj.mean_btw) || 0,
                    maxBtw: parseFloat(asObj.max_btw) || 0,
                    percentile: parseInt(asObj.pct, 10) || percentile,
                    scope: asObj.scope || "intra",
                    density: parseFloat(asObj.density) || 0,
                },
            };
        }
        return { rows: tableData, summary: summaryProp || null };
    }, [tableData, summaryProp, percentile]);

    const pct = summary?.percentile ?? percentile;

    // --- Custom Renderers ---

    const hubBody = (row) =>
        row.col5 === "Yes" ? (
            <Tag value={`Top ${pct}%`} severity="warning" rounded icon="pi pi-star-fill" />
        ) : (
            <Tag value="No" severity="secondary" rounded />
        );

    const pathFracBody = (row) => {
        const v = Number(row.col4);
        return Number.isFinite(v) ? `${(v * 100).toFixed(4)}%` : "-";
    };

    // Renderer for AlphaMissense Class
    const amClassBody = (row) => {
        if (!row.amClass) return <span className="text-muted small">N/A</span>;
        let severity = "secondary";
        if (row.amClass === "likely_pathogenic") severity = "danger";
        if (row.amClass === "likely_benign") severity = "success";
        if (row.amClass === "ambiguous") severity = "warning";
        return <Tag value={row.amClass.replace('_', ' ')} severity={severity} size="small" />;
    };

    // Renderer for MoCHI numerical values (with color for negative impact)
    const mochiBody = (val) => {
        if (val === undefined || val === null) return <span className="text-muted small">N/A</span>;
        const num = Number(val);
        const isDeleterious = num < -1.0;
        return <span className={isDeleterious ? "text-danger fw-bold" : ""}>{num.toFixed(2)}</span>;
    };

    // Renderer for AM Pathogenic Fraction (simple progress bar)
    const amFracBody = (row) => {
        if (row.amPathogenicFrac === undefined) return <span className="text-muted small">N/A</span>;
        const val = (Number(row.amPathogenicFrac) * 100).toFixed(0);
        return (
            <div className="d-flex align-items-center gap-2">
                <div className="progress w-100" style={{ height: '6px' }}>
                    <div className="progress-bar bg-danger" style={{ width: `${val}%` }}></div>
                </div>
                <small>{val}%</small>
            </div>
        );
    };

    const actionBody = (row) => (
        <Button
            label="View"
            icon="pi pi-sitemap"
            size="small"
            text
            onClick={() => onSelectResidue && onSelectResidue(row.col1)}
        />
    );

    // Dynamic header with MultiSelect
    const header = (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <MultiSelect
                value={selectedColumns}
                options={integrativeColumns}
                optionLabel="header"
                onChange={(e) => setSelectedColumns(e.value)}
                placeholder="Add Analyses"
                display="chip"
                className="w-full md:w-25rem"
                style={{ maxWidth: '450px' }}
            />
        </div>
    );

    return (
        <div>

            <DataTable
                value={rows}
                header={header}
                paginator
                rows={5}
                stripedRows
                showGridlines
                loading={loading}
                emptyMessage="No SPM data yet."
                size="small"
                className="mb-4"
                responsiveLayout="scroll"
            >
                {/* Base SPM Columns */}
                <Column field="col6" header="Rank" sortable body={(r) => <span className="text-secondary font-mono">#{r.col6}</span>} style={{ width: "5rem" }} />
                <Column field="col1" header="Residue" sortable body={(r) => <span className="fw-bold text-primary">{r.col1}</span>} />
                <Column field="col2" header="Degree" sortable />
                <Column field="col3" header="Betweenness" sortable body={(r) => <strong>{Number(r.col3).toFixed(2)}</strong>} />
                <Column field="col4" header="Path Fraction" sortable body={pathFracBody} />
                <Column field="col5" header="Hub Status" sortable body={hubBody} />

                {/* Dynamic Integrative Columns */}
                {selectedColumns.map((col) => {
                    let body = undefined;
                    if (col.field === "amClass") body = amClassBody;
                    if (col.field === "mochi_folding") body = (r) => mochiBody(r.mochi_folding);
                    if (col.field === "mochi_binding") body = (r) => mochiBody(r.mochi_binding);
                    if (col.field === "amPathogenicFrac") body = amFracBody;
                    if (col.field === "pfam") body = (r) => r.pfam || <span className="text-muted">N/A</span>;

                    return (
                        <Column
                            key={col.field}
                            field={col.field}
                            header={col.header}
                            sortable
                            body={body}
                        />
                    );
                })}

                <Column header="Ego" body={actionBody} style={{ width: "5rem" }} exportable={false} />
            </DataTable>

             {summary && (
                <div className="row g-3 mb-3">
                <h4 className="mb-3">
                                                                <i className="pi pi-sitemap me-2 text-primary" />
                                                                SPM profile
                                                            </h4>
                    <SummaryStat label="Total residues" value={summary.totalResidues} />
                    <SummaryStat label={`SPM hubs (top ${pct}%)`} value={summary.hubCount} accent="warning" />
                    <SummaryStat label="Mean betweenness" value={summary.meanBtw?.toFixed(2)} />
                    <SummaryStat label="SPM edges" value={summary.spmEdges} accent="info" />
                </div>
            )}
        </div>
    );
};

// Sub-component for summary cards
const SummaryStat = ({ label, value, accent }) => {
    const accentClass =
        accent === "warning" ? "border-warning" : accent === "info" ? "border-info" : "border-secondary";
    return (
        <div className="col-6 col-md-3">
            <div className={`card p-3 border-start border-3 ${accentClass} h-100`}>
                <div className="text-secondary small mb-1">{label}</div>
                <div className="fs-4 fw-bold">{value ?? "-"}</div>
            </div>
        </div>
    );
};

export default SpmProfileTablePath;